<template>
  <div class="kinematic-graphs-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$v = ds/dt | a = dv/dt$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="graphs-grid">
           <!-- s-t Graph -->
           <div class="graph-item">
              <label>位移-时间 (s-t)</label>
               <svg width="100%" height="130" viewBox="0 0 200 130">
                 <defs>
                    <linearGradient id="sLineGrad" x1="0" y1="0" x2="0" y2="1">
                       <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.3" />
                       <stop offset="100%" stop-color="#38bdf8" stop-opacity="0" />
                    </linearGradient>
                 </defs>
                 <path :d="stAreaPath" fill="url(#sLineGrad)" />
                 <line x1="20" y1="110" x2="190" y2="110" stroke="#475569" stroke-width="1.5" />
                 <line x1="20" y1="10" x2="20" y2="110" stroke="#475569" stroke-width="1.5" />
                 <path :d="stPath" fill="none" stroke="#0ea5e9" stroke-width="2" />
                 <circle :cx="20 + t*1.5" :cy="110 - currentS/2.5" r="3" fill="#0ea5e9" v-if="active" />
                 <text x="185" y="122" font-size="8" fill="#94a3b8">t</text>
                 <text x="5" y="15" font-size="8" fill="#94a3b8">s</text>
              </svg>
           </div>
           
           <!-- v-t Graph -->
           <div class="graph-item">
              <label>速度-时间 (v-t)</label>
              <svg width="100%" height="130" viewBox="0 0 200 130">
                 <line x1="20" y1="110" x2="190" y2="110" stroke="#475569" stroke-width="1.5" />
                 <line x1="20" y1="10" x2="20" y2="110" stroke="#475569" stroke-width="1.5" />
                 <!-- Area represents displacement -->
                 <path :d="vtAreaPath" fill="rgba(239, 68, 68, 0.1)" />
                 <path :d="vtPath" fill="none" stroke="#ef4444" stroke-width="2" />
                 <circle :cx="20 + t*1.5" :cy="110 - currentV*1.5" r="3" fill="#ef4444" v-if="active" />
                 <text x="185" y="122" font-size="8" fill="#94a3b8">t</text>
                 <text x="5" y="15" font-size="8" fill="#94a3b8">v</text>
              </svg>
           </div>
        </div>

        <!-- Animation Area -->
        <div class="animation-track">
           <div class="track-info">物理空间实时演示</div>
           <div class="track"></div>
           <div class="track-markers">
              <span v-for="m in 6" :key="m" :style="{left: (m-1)*60 + 20 + 'px'}">{{(m-1)*50}}m</span>
           </div>
           <div class="car" :style="{ transform: `translateX(${20 + currentS*1.2}px)` }">
              <div class="car-body">
                 <div class="car-window"></div>
              </div>
              <div class="car-wheel car-wheel-left" :style="{transform: `rotate(${t*10}deg)`}"></div>
              <div class="car-wheel car-wheel-right" :style="{transform: `rotate(${t*10}deg)`}"></div>
              <div class="v-vector" v-if="currentV > 0" :style="{ width: currentV * 3 + 'px' }"></div>
           </div>
        </div>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>实时位置 s</label>
              <span class="val">{{ currentS.toFixed(1) }} <small>m</small></span>
           </div>
           <div class="hud-item orange">
              <label>瞬时速度 v</label>
              <span class="val">{{ currentV.toFixed(1) }} <small>m/s</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item" style="grid-column: span 2;">
                 <label>选择运动模式：</label>
                 <el-radio-group v-model="motionType" @change="reset" size="small">
                    <el-radio-button label="constV">匀速 (斜率恒定)</el-radio-button>
                    <el-radio-button label="constA">匀加速 (抛物线/斜线)</el-radio-button>
                    <el-radio-button label="decel">匀减速 (末速归零)</el-radio-button>
                 </el-radio-group>
              </div>
              <div class="s-item flex flex-row items-center justify-center" style="grid-column: span 2; border-top:1px solid #f1f5f9; padding-top:10px;">
                 <el-button type="primary" size="small" @click="toggle" :disabled="t > 100" icon="el-icon-video-play">
                    {{ running ? '暂停' : '启动模拟' }}
                 </el-button>
                 <el-button size="small" @click="reset" icon="el-icon-refresh-right">重置数据</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>[1] <strong>s-t 图像的斜率</strong>代表速度，图线越陡峭速度越快；[2] <strong>v-t 图像的斜率</strong>代表加速度，而其<strong>图线下的面积</strong>则代表了物体在这段时间内的总位移。注意观察匀加速过程，s-t呈曲线而v-t呈直线，这完美对应了微积分中的积分关系。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KinematicGraphsViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      motionType: 'constV',
      t: 0,
      running: false,
      history: [],
      timer: null
    }
  },
  computed: {
    active() { return this.t > 0; },
    currentS() {
      const timeStep = this.t / 8;
      if (this.motionType === 'constV') return 12 * timeStep;
      if (this.motionType === 'constA') return 0.5 * 3.5 * timeStep * timeStep;
      if (this.motionType === 'decel') {
         let v0 = 24, a = -3.2;
         const tStop = -v0/a;
         const effT = Math.min(timeStep, tStop);
         return v0 * effT + 0.5 * a * effT * effT;
      }
      return 0;
    },
    currentV() {
      const timeStep = this.t / 8;
      if (this.motionType === 'constV') return 12;
      if (this.motionType === 'constA') return 3.5 * timeStep;
      if (this.motionType === 'decel') return Math.max(0, 24 - 3.2 * timeStep);
      return 0;
    },
    stPath() {
       if (this.history.length === 0) return '';
       let d = `M ${20 + this.history[0].t*1.5} ${110 - this.history[0].s/2.5}`;
       this.history.forEach(p => { d += ` L ${20 + p.t*1.5} ${110 - p.s/2.5}`; });
       return d;
    },
    stAreaPath() {
       if (this.history.length === 0) return '';
       let d = this.stPath;
       d += ` L ${20 + this.history[this.history.length-1].t*1.5} 110 L 20 110 Z`;
       return d;
    },
    vtPath() {
       if (this.history.length === 0) return '';
       let d = `M ${20 + this.history[0].t*1.5} ${110 - this.history[0].v*1.5}`;
       this.history.forEach(p => { d += ` L ${20 + p.t*1.5} ${110 - p.v*1.5}`; });
       return d;
    },
    vtAreaPath() {
       if (this.history.length === 0) return '';
       let d = this.vtPath;
       d += ` L ${20 + this.history[this.history.length-1].t*1.5} 110 L 20 110 Z`;
       return d;
    }
  },
  methods: {
    toggle() {
      if (this.t > 100) return;
      this.running = !this.running;
      if (this.running) this.animate();
    },
    animate() {
       if (!this.running) return;
       this.t += 0.5;
       this.history.push({ t: this.t, s: this.currentS, v: this.currentV });
       
       if (this.t >= 100 || (this.motionType === 'decel' && this.currentV <= 0)) {
          this.running = false;
       } else {
          this.timer = requestAnimationFrame(this.animate);
       }
    },
    reset() {
       this.running = false;
       this.t = 0;
       this.history = [];
       if (this.timer) cancelAnimationFrame(this.timer);
    }
  },
  beforeDestroy() { if (this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.kinematic-graphs-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 320px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; padding: 12px; }
.graphs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
.graph-item { 
   background: rgba(30, 41, 59, 0.5); padding: 8px; border-radius: 8px; border: 1px solid #334155;
   label { font-size: 11px; color: #94a3b8; font-weight: 700; display: block; margin-bottom: 4px; }
}
.animation-track { 
   height: 80px; position: relative; background: rgba(15, 23, 42, 0.8); border-radius: 10px; border: 1px solid #1e293b; overflow: hidden;
   .track-info { position: absolute; top: 8px; left: 16px; font-size: 10px; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
   .track { position: absolute; bottom: 24px; left: 20px; right: 20px; height: 4px; background: #334155; border-radius: 2px; }
   .track-markers { position: absolute; bottom: 8px; left: 20px; right: 20px; display: flex; font-size: 9px; color: #475569; font-family: monospace; }
   .car { 
      position: absolute; bottom: 25px; width: 36px; height: 20px; transition: transform 0.05s linear;
      .car-body { width: 100%; height: 12px; background: #38bdf8; border-radius: 4px 6px 2px 2px; position: relative; box-shadow: 0 4px 12px rgba(56, 189, 248, 0.2); }
      .car-window { position: absolute; top: 2px; right: 4px; width: 12px; height: 4px; background: rgba(255,255,255,0.4); border-radius: 1px; }
      .car-wheel { position: absolute; bottom: -5px; width: 10px; height: 10px; background: #0f172a; border-radius: 50%; border: 2px solid #334155; }
      .car-wheel-left { left: 4px; }
      .car-wheel-right { right: 4px; }
      .v-vector { 
         position: absolute; top: 6px; left: 100%; height: 2px; background: #ef4444; box-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
         &::after { content: ''; position: absolute; right: -4px; top: -3px; border: 4px solid transparent; border-left-color: #ef4444; }
      }
   }
}
.hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 6px 12px; min-width: 100px; label { display: block; font-size: 9px; color: #64748b; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.flex { display: flex; } .items-center { align-items: center; } .justify-center { justify-content: center; }
.discovery-card-compact { background: #f0fdfa; padding: 10px 14px; border-radius: 10px; border: 1px solid #ccfbf1; display: flex; align-items: flex-start; gap: 10px; i { color: #0d9488; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #0f766e; line-height: 1.5; strong { color: #115e59; } } }
</style>
